import { TEST_IDS } from '@va/constants';
import { AccordionSection, HorizontalSeparator, Paragraph, fontWeights, paragraphSizes } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import { useCheckOverflow, useIsFocused } from '@va/util/hooks';
import classNames from 'classnames';
import { Fragment, ReactNode, useRef } from 'react';

export type PrivacyLevelFeature = {
  title: string;
  titleClassName?: string;
  titleTooltip?: string | ReactNode;
  subTitle: string | ReactNode;
  subTitleTooltip?: string | ReactNode;
};

type PrivacyLevelFeatureCardTypes = {
  icon: React.ReactNode;
  title: string;
  titleClassName?: string;
  levelFeatures: PrivacyLevelFeature[];
  isInitiallyOpen?: boolean;
};
const PrivacyLevelFeatureCard = ({
  icon,
  title,
  levelFeatures,
  titleClassName,
  isInitiallyOpen = true,
}: PrivacyLevelFeatureCardTypes) => {
  return (
    <AccordionSection
      className='hover:bg-white!'
      heightClass='h-60px'
      bgColorClass='bg-white'
      icon={icon}
      title={title}
      isInitiallyOpen={isInitiallyOpen}
      titleClassName={titleClassName}
      data-testid={TEST_IDS.generic.privacyLevelCard.featuresCard}
    >
      <div className='flex flex-col'>
        {levelFeatures.map(({ title, titleTooltip, subTitle, subTitleTooltip, titleClassName }, index) => {
          const isLast = levelFeatures.length === index + 1;
          return (
            <Fragment key={index}>
              <div className='flex flex-col px-18px py-[0.7188rem] text-center w-full'>
                <TitleSection title={title} titleTooltip={titleTooltip} className={titleClassName} />

                <SubTitleSection subTitle={subTitle} subTitleTooltip={subTitleTooltip} />
              </div>
              {!isLast && <HorizontalSeparator />}
            </Fragment>
          );
        })}
      </div>
    </AccordionSection>
  );
};

export default PrivacyLevelFeatureCard;

const TitleSection = ({
  title,
  titleTooltip,
  className,
}: {
  title: string;
  titleTooltip: string | ReactNode;
  className?: string;
}) => {
  const titleRef = useRef<HTMLParagraphElement>(null);
  const isTitleOverflowing = useCheckOverflow(titleRef);
  const isFocused = useIsFocused(titleRef);

  return (
    <TooltipWrapper
      trigger='click'
      interactive
      tooltipClassNames={classNames({ 'rounded-3xl!': typeof titleTooltip === 'object' })}
      content={titleTooltip ? titleTooltip : title}
      disabled={!titleTooltip && !isTitleOverflowing}
      open={isFocused || undefined}
    >
      <Paragraph
        size={paragraphSizes.tiny}
        className={classNames('truncate w-full', { 'underline-dashed': titleTooltip }, className)}
        colorClassName='text-gray-devil'
        weight={fontWeights.normal}
        ref={titleRef}
        tabIndex={titleTooltip ? 0 : undefined}
      >
        {title}
      </Paragraph>
    </TooltipWrapper>
  );
};

const SubTitleSection = ({
  subTitle,
  subTitleTooltip,
}: {
  subTitle: string | ReactNode;
  subTitleTooltip: string | ReactNode;
}) => {
  const subtitleRef = useRef<HTMLParagraphElement>(null);
  const isSubtitleOverflowing = useCheckOverflow(subtitleRef);
  const isFocused = useIsFocused(subtitleRef);

  if (typeof subTitle !== 'string') {
    return <div className='min-h-24px flex items-center justify-center w-full'>{subTitle}</div>;
  }

  return (
    <TooltipWrapper
      interactive
      trigger='click'
      tooltipClassNames={classNames({ 'rounded-24!': typeof subTitleTooltip === 'object' })}
      content={subTitleTooltip ? subTitleTooltip : subTitle}
      disabled={!subTitleTooltip && !isSubtitleOverflowing}
      open={isFocused || undefined}
    >
      <Paragraph
        className={classNames('truncate ', {
          'underline-dashed': subTitleTooltip,
        })}
        colorClassName='var(--color-text-primary)'
        weight={fontWeights.medium}
        ref={subtitleRef}
        tabIndex={subTitleTooltip ? 0 : undefined}
      >
        {subTitle}
      </Paragraph>
    </TooltipWrapper>
  );
};
